import { DEFAULT_WEEK_START_DAY } from "@common/consts/view-consts";
import type { SharedTodoStoreDependencies } from "@common/stores/shared-todo-store";
import SharedTodoStore from "@common/stores/shared-todo-store";
import type { GroupKey, HashtagMetaValueType, MetaItem, Todo, TodoId } from "@common/types/todo";
import { getTodoHashtagsStr } from "@common/types/todo";
import { shouldPaywallOnCreateTodo } from "@common/use-cases/should-paywall";
import type { Direction, DisplayMode} from "@common/utils/todo-utils";
import TodoUtils, { nullFilterFn } from "@common/utils/todo-utils";
import { analytics } from "@web/src/app/use-cases/analytics";
import { action, computed, makeObservable } from "mobx";
import { WebEvents } from "../app/web-events";
import type HighlightStore from "./highlight-store";
import TodoGroupUtils, { PRESET_GROUP_KEYS } from "@common/utils/todo-group-utils";
import { todoIdToHighlightId } from "@web/src/app/use-cases/todo-highlight";
import { addTodoHighlightableId } from "@web/src/app/components/add-todo-button";

interface WebTodoStoreDependencies extends SharedTodoStoreDependencies {
  webEvents: typeof WebEvents;
  highlightStore: HighlightStore;
}
export default class WebTodoStore extends SharedTodoStore<WebTodoStoreDependencies> {
  constructor(spaceId: string, dependencies: WebTodoStoreDependencies) {
    super(spaceId, dependencies);
    makeObservable(this);

    if (typeof window !== "undefined") {
      window.addEventListener("focus", async () => {
        this.rerenderTodos();
      });
    }
  }

  // @action
  // async loadTodos() {
  //   runInAction(() => {
  //     this._lastLoadTime = new Date();
  //     this.isLoading = true;
  //   });

  //   try {
  //     let todoIdMap = await this.deps.todoService.getTodos(this.spaceId, this.deps.userStore.timezone);
  //     if (!todoIdMap) {
  //       return;
  //     }
  //     this.setTodoIdMapHard(todoIdMap);
  //   } finally {
  //     setTimeout(() => {
  //       runInAction(() => {
  //         this.isLoading = false;
  //       });
  //     }, 500);
  //   }
  // }

  todoExists(todoId: TodoId, todoIdMap = this.todoIdMap) {
    return todoIdMap[todoId] !== undefined && todoIdMap[todoId]?.deleted == false;
  }
  @action
  setDisplayMode(mode: DisplayMode) {
    if (this.displayMode === mode) return;

    analytics.track("set_display_mode", { mode });
    this.displayMode = mode;

    if (mode === "calendar") {
      const today = new Date();
      this.setCalendarViewCurrentDate(today);

      // Next frame, after re-render
      // Set highlight to To-day
      const calendarTodayGroupKey = TodoGroupUtils.groupKeyForDate(today, "calendar");
      if (!calendarTodayGroupKey) throw new Error("Unexpected invalid date");
      const todayTodos = TodoGroupUtils.todoListForGroup(
        calendarTodayGroupKey,
        this.todoIdMap,
        this.curHashtagFilterFn,
        "calendar"
      );
      const highlightId = todayTodos[0]?.id
        ? todoIdToHighlightId(todayTodos[0].id, calendarTodayGroupKey)
        : addTodoHighlightableId(calendarTodayGroupKey);
      this.deps.highlightStore.setCurrentHighlightId(highlightId);
      setTimeout(() => {});
    }
  }

  @computed
  get currentTodo() {
    if (this.deps.highlightStore.currentHighlightObj?.kind !== "todo") return null;
    const todoId = this.deps.highlightStore.currentHighlightObj.data.todoId;
    if (!todoId || !this.todoIdMap[todoId]) return null;

    const isPlaceholder = this.todoIdMap[todoId] && !this.todoIdMap[todoId];
    if (!this.todoExists(todoId) && !isPlaceholder) {
      return null;
    }

    return this.todoIdMap[todoId] ?? null;
  }
  @action
  loadSettings() {
    if (typeof window !== "undefined") {
      // this.copyHashtag = (localStorage.getItem("copy-hashtag") ?? "true") == "true";
    }
  }

  indentTodo(todoId: TodoId, offset: number, groupKey: GroupKey) {
    try {
      super.indentTodo(todoId, offset, groupKey);
    } catch (error) {
      if (["no_previous", "max_indent"].includes(error as string)) {
        this.deps.commonEvents.emit("PUBLISH_TOAST", {
          title: "Cannot indent",
          content: "This todo cannot be indented further",
          duration: 3000,
        });
      } else if (error === "no_parent") {
        this.deps.commonEvents.emit("PUBLISH_TOAST", {
          title: "Cannot unindent",
          content: "This todo cannot be unindented further",
          duration: 3000,
        });
      } else {
        console.error(error);
        this.deps.commonEvents.emit("PUBLISH_TOAST", {
          title: "Error",
          content: (error as Error).message || "Something went wrong",
          titleColor: "negative",
        });
      }
    }
  }

  createTodoAfter(afterTodo: Todo, groupKey: GroupKey, copyHashtags?: boolean) {
    if (shouldPaywallOnCreateTodo(this.fullTodoIdMap, Boolean(this.deps.userStore.isPremium))) {
      this.deps.commonEvents.emit("PAYWALL", { from: "add_todo" });
      return;
    }

    // creating after current has different scenarios:
    // 1. it is a child => created todo should have same parent
    // 2. it is a parent
    // 2 - 2a. is collapsed => created todo should insert after with same parent
    // 2 - 2b. else => created todo should be first child
    // if there are filters on, new todos are created with that filter
    let refTodo = afterTodo;

    const shouldCopyHashtag = copyHashtags === undefined ? this.copyHashtag : copyHashtags;
    // copy hashtags
    let hashtagStrings = shouldCopyHashtag ? getTodoHashtagsStr(refTodo) : [];
    hashtagStrings.push(...this.hashtagFilters);
    hashtagStrings = Array.from(new Set(hashtagStrings)); // remove duplicates
    const meta: MetaItem<HashtagMetaValueType>[] = hashtagStrings.map((hashtag) => ({
      metaType: "HASHTAG",
      value: hashtag,
    }));
    let newTodo = TodoUtils.createTodo({
      value: "",
      owner: this.deps.userStore.userId,
      space: this.spaceId,
      meta,
    });

    let isParent =
      (Object.values(this.todoIdMap) as Todo[]).find((todo: Todo) => {
        return todo?.parent && todo?.parent === refTodo?.id;
      }) !== undefined;
    let shouldInsertAsChild = isParent && !refTodo?.collapsed;

    let parentTodoId = (shouldInsertAsChild ? refTodo?.id : refTodo?.parent) || null; // defult to outermost
    let deltaTodoIdMap = TodoUtils.insertTodosAfter(
      this.todoIdMap,
      [newTodo],
      parentTodoId,
      afterTodo.id,
      groupKey,
      this.displayMode,
      this.curHashtagFilterFn
    );

    this.createTodo(newTodo);
    this.updateTodos(deltaTodoIdMap);

    return newTodo;
  }

  moveTodo(todoId: TodoId, groupKey: GroupKey, direction: Direction) {
    const { delta: updateDelta, updatedGroupKey } = TodoUtils.moveTodoInDirection(
      todoId,
      groupKey,
      direction,
      this.displayMode,
      { referenceDate: this.calendarViewCurrentDate, firstDayInWeek: DEFAULT_WEEK_START_DAY },
      this.hashtagFilters,
      this.hashtagMetas,
      this.curHashtagFilterFn,
      this.todoIdMap
    );
    if (!Object.keys(updateDelta).length) return groupKey;
    this.updateTodos(updateDelta);
    WebEvents.emit("WEB_ON_MOVED_TODO", todoId);
    return updatedGroupKey;
  }

  /**
   * Move todos to date, ignoring displayMode and hashtag filter functions
   */
  moveTodosToDate(todoIds: TodoId[], date: Date | null) {
    // We need to use "week" display mode for someday
    // For all actual dates, we use calendar mode
    const requiredDisplayMode: DisplayMode = date === null ? "week" : "calendar";
    const groupKey = TodoGroupUtils.groupKeyForDate(date, requiredDisplayMode);
    if (!groupKey) throw new Error(`Unexpected invalid group key`);
    const delta = TodoUtils.moveTodosToGroup(this.fullTodoIdMap, todoIds, groupKey, requiredDisplayMode, nullFilterFn);
    this.updateTodos(delta);
  }

  moveTodosToGroup(todoIds: TodoId[], groupKey: GroupKey, displayMode: DisplayMode) {
    const delta = TodoUtils.moveTodosToGroup(this.fullTodoIdMap, todoIds, groupKey, displayMode, nullFilterFn);
    this.updateTodos(delta);
  }
}
